import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, ScanCommand } from '@aws-sdk/lib-dynamodb';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PDFBucket, HTMLBucket, region, MetadataTableName, PRIMARY_MAIN, LINK_COLOR } from '../utilities/constants';
import AccessibilityChecker from './AccessibilityChecker';

const PastFilesList = ({ awsCredentials }) => {
  const auth = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDialogFile, setReportDialogFile] = useState(null);

  const userEmail = auth.user?.profile?.email || '';
  const sanitizedEmail = userEmail.replace(/[^a-zA-Z0-9]/g, '_');

  // Extract original filename from DynamoDB file_key.
  // file_key formats:
  //   pdf2pdf:  pdf/{sanitizedEmail}_{17digitTimestamp}_{originalFilename}
  //   pdf2html: uploads/{sanitizedEmail}_{17digitTimestamp}_{originalFilename}
  const parseFileKey = (file_key) => {
    const withoutPrefix = file_key.replace(/^(pdf|uploads)\//, '');
    const match = withoutPrefix.match(/^(.+?)_(\d{17})_(.+)$/);
    if (match) {
      return match[3]; // original filename
    }
    return withoutPrefix;
  };

  // Derive download bucket and output S3 key from a DynamoDB item.
  // If output_key is stored in the record (written by COMPLETED update), use it directly.
  // Otherwise fall back to convention-based derivation.
  const getDownloadInfo = (file) => {
    if (file.remediationType === 'pdf2html') {
      // pdf2html output: stored in HTMLBucket under remediated/final_{basename}.zip
      const outputKey = file.output_key ||
        `remediated/final_${file.file_key.replace(/^uploads\//, '').replace(/\.pdf$/i, '')}.zip`;
      return { bucket: HTMLBucket, outputKey, filename: `${file.originalName.replace(/\.pdf$/i, '')}.zip` };
    }
    // pdf2pdf output: stored in PDFBucket under result/COMPLIANT_{original key without prefix}
    const outputKey = file.output_key ||
      file.file_key.replace(/^pdf\//, 'result/COMPLIANT_');
    return { bucket: PDFBucket, outputKey, filename: `COMPLIANT_${file.originalName}` };
  };

  // Derive the COMPLIANT PDF filename (no "COMPLIANT_" prefix in the base)
  // for a given file record, to feed into AccessibilityChecker as updatedFilename.
  const getUpdatedFilenameForFile = (file) => {
    // Prefer the explicit output_key written by the post-check lambda, if present.
    const key = file.output_key || file.file_key.replace(/^pdf\//, 'result/COMPLIANT_');
    const parts = key.split('/');
    const baseName = parts[parts.length - 1];
    return baseName.replace(/^COMPLIANT_/, '');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fetchFiles = useCallback(async () => {
    if (!awsCredentials?.accessKeyId || !sanitizedEmail) {
      setError('AWS credentials or user email not available');
      setLoading(false);
      return;
    }
    if (!MetadataTableName) {
      setError('REACT_APP_METADATA_TABLE_NAME is not configured');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const dynamoClient = new DynamoDBClient({
        region,
        credentials: {
          accessKeyId: awsCredentials.accessKeyId,
          secretAccessKey: awsCredentials.secretAccessKey,
          sessionToken: awsCredentials.sessionToken,
        },
      });
      const docClient = DynamoDBDocumentClient.from(dynamoClient);

      // console.log('[PastFilesList] Fetching files for sanitizedEmail:', sanitizedEmail);
      // console.log('[PastFilesList] DynamoDB table:', MetadataTableName);
      // console.log('[PastFilesList] Filters — pdf prefix:', `pdf/${sanitizedEmail}_`, '| uploads prefix:', `uploads/${sanitizedEmail}_`);

      // Paginate through all DynamoDB scan results (each page is up to 1 MB).
      let allItems = [];
      let lastEvaluatedKey = undefined;
      do {
        const result = await docClient.send(
          new ScanCommand({
            TableName: MetadataTableName,
            // Fetch both pdf2pdf (pdf/ prefix) and pdf2html (uploads/ prefix) files for this user
            FilterExpression:
              'begins_with(file_key, :prefix_pdf) OR begins_with(file_key, :prefix_uploads)',
            ExpressionAttributeValues: {
              ':prefix_pdf': `pdf/${sanitizedEmail}_`,
              ':prefix_uploads': `uploads/${sanitizedEmail}_`,
            },
            ExclusiveStartKey: lastEvaluatedKey,
          })
        );
        console.log('[PastFilesList] Scan page — raw items:', result.Items?.map(i => ({
          file_key: i.file_key,
          status: i.status,
          remediation_type: i.remediation_type,
        })));
        allItems = allItems.concat(result.Items || []);
        lastEvaluatedKey = result.LastEvaluatedKey;
      } while (lastEvaluatedKey);

      console.log('[PastFilesList] Total items after pagination:', allItems.length);

      const items = allItems.map((item) => ({
        file_key: item.file_key,
        originalName: parseFileKey(item.file_key),
        uploadedAt: item.uploaded_at || '',
        formattedDate: item.uploaded_at
          ? new Date(item.uploaded_at).toLocaleString()
          : '—',
        pageCount: item.page_count ?? null,
        fileSize: item.file_size_bytes ?? null,
        status: item.status || 'PROCESSING',
        remediationType: item.remediation_type || 'pdf2pdf',
        output_key: item.output_key || null,
      }));

      // Newest first
      items.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
      setFiles(items);
    } catch (err) {
      console.error('Error fetching files from DynamoDB:', err);
      if (err.name === 'ResourceNotFoundException' || err.__type === 'ResourceNotFoundException') {
        setError(
          `Table "${MetadataTableName}" not found. The PDFAccessibility CDK stack needs to be deployed first.`
        );
      } else if (err.name === 'AccessDeniedException' || err.__type === 'AccessDeniedException') {
        setError('Access denied. Ensure the CdkBackendStack is redeployed to grant DynamoDB permissions.');
      } else {
        setError(`Failed to load files: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [awsCredentials, sanitizedEmail]);

  useEffect(() => {
    if (awsCredentials && sanitizedEmail) {
      fetchFiles();
    }
  }, [awsCredentials, sanitizedEmail, fetchFiles]);

  const handleMenuOpen = (event, file) => {
    setMenuAnchor(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedFile(null);
  };

  const handleViewAccessibilityReport = (file) => {
    // Only allow for completed PDF→PDF jobs; guard just in case.
    if (!file || file.remediationType !== 'pdf2pdf' || file.status !== 'COMPLETED') {
      return;
    }
    setReportDialogFile(file);
    setReportDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleDownload = async (file) => {
    handleMenuClose();

    if (!awsCredentials?.accessKeyId) {
      setError('AWS credentials not available');
      return;
    }

    const { bucket, outputKey, filename } = getDownloadInfo(file);
    if (!bucket || bucket === 'Null') {
      setError(`Bucket for ${file.remediationType} is not configured`);
      return;
    }

    setDownloadingFiles((prev) => new Set(prev).add(file.file_key));

    try {
      const s3 = new S3Client({
        region,
        credentials: {
          accessKeyId: awsCredentials.accessKeyId,
          secretAccessKey: awsCredentials.secretAccessKey,
          sessionToken: awsCredentials.sessionToken,
        },
      });

      const command = new GetObjectCommand({
        Bucket: bucket,
        Key: outputKey,
        ResponseContentDisposition: `attachment; filename="${filename}"`,
      });

      const url = await getSignedUrl(s3, command, { expiresIn: 3600 });

      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      if (err.$metadata?.httpStatusCode === 403 || err.$metadata?.httpStatusCode === 404) {
        setError(`"${file.originalName}" is not ready yet. Please check back later.`);
      } else {
        setError(`Failed to download: ${err.message}`);
      }
    } finally {
      setDownloadingFiles((prev) => {
        const next = new Set(prev);
        next.delete(file.file_key);
        return next;
      });
    }
  };

  const statusChipProps = (status) => {
    if (status === 'COMPLETED') return { color: 'success' };
    if (status === 'FAILED') return { color: 'error' };
    return { color: 'warning' };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          My Processed Files
        </Typography>
        <Tooltip title="Refresh list">
          <IconButton onClick={fetchFiles} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {files.length === 0 ? (
        <Alert severity="info">
          No files found. Upload and process a PDF to see it here.
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>File Name</strong></TableCell>
                <TableCell><strong> Output Format</strong></TableCell>
                <TableCell><strong>Pages</strong></TableCell>
                <TableCell><strong>Size</strong></TableCell>
                <TableCell><strong>Uploaded</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file, index) => (
                <TableRow key={`${file.file_key}-${index}`} hover>
                  <TableCell>{file.originalName}</TableCell>
                  <TableCell>
                    <Chip
                      label={file.remediationType === 'pdf2html' ? 'PDF → HTML' : 'PDF → PDF'}
                      size="small"
                      variant="outlined"
                      color={file.remediationType === 'pdf2html' ? 'secondary' : 'primary'}
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        color: file.remediationType === 'pdf2html' ? LINK_COLOR : PRIMARY_MAIN,
                        backgroundColor: alpha(file.remediationType === 'pdf2html' ? LINK_COLOR : PRIMARY_MAIN, 0.18),
                        borderColor: file.remediationType === 'pdf2html' ? LINK_COLOR : PRIMARY_MAIN
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {Number.isFinite(file.pageCount) && file.pageCount > 0 ? file.pageCount : '—'}
                  </TableCell>
                  <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                  <TableCell>{file.formattedDate}</TableCell>
                  <TableCell>
                    {(() => {
                      const { color } = statusChipProps(file.status);
                      return (
                        <Chip
                          label={file.status}
                          size="small"
                          variant="outlined"
                          color={color}
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            color: (theme) => theme.palette[color].dark || theme.palette[color].main,
                            backgroundColor: (theme) => alpha(theme.palette[color].main, 0.18),
                            border: (theme) => `1px solid ${theme.palette[color].main}`,
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, file)}
                      size="small"
                      aria-label="File actions"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => selectedFile && handleDownload(selectedFile)}
          disabled={selectedFile && downloadingFiles.has(selectedFile.file_key)}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedFile && downloadingFiles.has(selectedFile.file_key)
              ? 'Downloading...'
              : 'Download'}
          </ListItemText>
        </MenuItem>
        {selectedFile &&
          selectedFile.remediationType === 'pdf2pdf' &&
          selectedFile.status === 'COMPLETED' && (
            <MenuItem onClick={() => handleViewAccessibilityReport(selectedFile)}>
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                View Accessibility Report
              </ListItemText>
            </MenuItem>
          )}
      </Menu>

      {reportDialogFile && (
        <AccessibilityChecker
          originalFileName={reportDialogFile.originalName}
          updatedFilename={getUpdatedFilenameForFile(reportDialogFile)}
          awsCredentials={awsCredentials}
          open={reportDialogOpen}
          onClose={() => {
            setReportDialogOpen(false);
            setReportDialogFile(null);
          }}
        />
      )}
    </Box>
  );
};

export default PastFilesList;
